import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextField, Typography, Link, Box, Paper, CircularProgress } from '@mui/material';
import { CheckCircleFilled } from '@ant-design/icons';
import axios from 'axios';
import loginBackground from '../assets/login3.png';
import ksgLogo from '../assets/ksg-logo.png'; // adjust path as needed

function Login() {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [resetToken, setResetToken] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpValid, setOtpValid] = React.useState(false);
  const [loading, setLoading] = React.useState(false); // Loading state for spinner
  const [errors, setErrors] = React.useState({
    email: '',
    password: '',
    username: '',
    resetToken: '',
    newPassword: '',
    confirmPassword: '',
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  const formStyle = {
    width: '100%',
    maxWidth: 350,
    bgcolor: 'transparent',
    p: 2,
    mx: 'auto',
    borderRadius: 2,
    boxShadow: 3,
    zIndex: 2, // Ensure the form appears above the background
    backdropFilter: 'blur(6px)', // Optional: Adds blur effect to the background
    border: '1px solid rgba(255, 255, 255, 0.3)', // Optional: Border for the form for contrast
  };

  const buttonStyle = {
    borderRadius: 25,
    padding: '10px 20px',
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    let hasError = false;
    const newErrors = { username: '', password: '' };

    if (!username) {
      newErrors.username = 'Username is required';
      hasError = true;
    }
    if (!password) {
      newErrors.password = 'Password is required';
      hasError = true;
    }
    setErrors(newErrors);

    if (hasError) return;

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/v1/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      console.log('data', data);

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      const { role, userName, emailId, clientID, brokerID } = data;
      localStorage.setItem('role', role);
      localStorage.setItem('isLoggedIn', true);
      localStorage.setItem('userName', userName);
      localStorage.setItem('email', emailId);
      localStorage.setItem('clientId', clientID);
      localStorage.setItem('brokerID', brokerID);

      if (role === 'Super Admin') {
        navigate(`/manage-dashboard`);
      } else if (role === 'Employee') {
        navigate(`/manage-dashboard`);
      } else if (role === 'Admin') {
        navigate(`/fuel-card`);
      } else if (role === 'User') {
        navigate(`/fuel-card`);
      } else if (role === 'Broker') {
        navigate(`/manage-brokers/list`);
      } else {
        navigate('/unknown');
      }
    } catch (error) {
      setErrors({ ...errors, password: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    let hasError = false;
    const newErrors = { username: '', email: '' };

    if (!username) {
      newErrors.username = 'Username is required';
      hasError = true;
    }
    if (!email) {
      newErrors.email = 'Email is required';
      hasError = true;
    }
    setErrors(newErrors);

    if (hasError) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/v1/forgot-password?username=${username}&emailId=${email}`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to send reset email. Update a valid Email for the username');
      }

      setMessage('OTP sent valid for 5 mins. Please check your email.');
      setOtpSent(true);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOtp = async () => {
    if (!resetToken) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        resetToken: 'OTP is required',
      }));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/v1/validate-otp?username=${username}&otp=${resetToken}`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        throw new Error('Invalid OTP');
      }

      setOtpValid(true); // Mark OTP as validated
      setMessage('OTP validated successfully. You can now set a new password.');
    } catch (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        resetToken: error.message,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: 'Passwords do not match' }));
      return;
    }

    if (!newPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: 'New password is required',
      }));
      return;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      newPassword: '',
      confirmPassword: '',
    }));

    setLoading(true);

    try {
      if (!otpValid) {
        setMessage('Please validate OTP first.');
        return;
      }

      const resetResponse = await fetch(
        `${BACKEND_URL}/v1/reset-password?username=${username}&token=${resetToken}&newPassword=${newPassword}`,
        {
          method: 'POST',
        },
      );

      if (!resetResponse.ok) {
        throw new Error('Failed to update password');
      }

      setMessage('Password updated successfully');
      setTimeout(() => {
        navigate('/');
        setShowForgotPassword(false); // Ensure to reset state to show login form
      }, 2000); // Redirect to login after 2 seconds
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (event) => {
    const value = event.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setResetToken(value);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        display: 'flex',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        alignItems: 'center',
        backgroundImage: { xs: 'none', sm: `url(${loginBackground})` }, // Remove background image on mobile
        backgroundColor: { xs: '#334197', sm: 'transparent' }, // Blue background on mobile portrait
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        margin: 0,
        padding: { xs: 2, sm: 0 },
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Paper
        sx={{
          padding: { xs: 2.5, sm: 3 },
          width: { xs: 'min(92vw, 380px)', sm: 'min(36vw, 420px)', md: 380 },
          maxWidth: 'calc(100vw - 32px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: { xs: 'rgba(48, 63, 159, 0.96)', sm: 'rgba(48, 63, 159, 0.88)' },
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          zIndex: 2,
          marginRight: { xs: 0, sm: 'clamp(24px, 8vw, 180px)' },
          marginTop: { xs: 4, sm: 0 },
          marginLeft: 0,
          justifyContent: 'center', // Center content vertically
          boxSizing: 'border-box',
        }}
      >

        <img src={ksgLogo} alt="KSG Logo" style={{ width: '120px', marginBottom: '20px' }} />


        {!showForgotPassword ? (
            <>
              <Typography
                align="center"
                sx={{
                  mb: 2,
                  fontFamily: 'sans-serif',
                  color: '#FFFFFF',
                  fontSize: '24px',
                }}
              >
                LOGIN
              </Typography>
              <form onSubmit={handleLogin} style={{ width: '100%' }}>
                <TextField
                  id="login-username"
                  placeholder="Username"
                  onChange={(event) => setUsername(event.target.value)}
                  value={username}
                  fullWidth
                  sx={{
                    width: '100%',
                    mb: 2,
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ccc',
                      },
                      '&:hover fieldset': {
                        borderColor: '#999',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: '8px', // Reduce vertical padding to decrease height
                      fontSize: '0.875rem', // Optional: Adjust input text size
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '0.75rem', // Smaller placeholder font size
                      opacity: 1,
                    },
                  }}
                  autoComplete="username"
                  error={!!errors.username}
                  helperText={errors.username}
                  size="small"
                />
                <TextField
                  id="login-password"
                  placeholder="Password"
                  type="password"
                  variant="outlined"
                  onChange={(event) => setPassword(event.target.value)}
                  value={password}
                  fullWidth
                  sx={{
                    width: '100%',
                    mb: 2,
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ccc',
                      },
                      '&:hover fieldset': {
                        borderColor: '#999',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: '8px', // Reduce vertical padding to decrease height
                      fontSize: '0.875rem', // Optional: Adjust input text size
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '0.75rem', // Smaller placeholder font size
                      opacity: 1,
                    },
                  }}
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password}
                  size="small"
                />
                <Button
                  color="primary"
                  type="submit"
                  onClick={handleLogin}
                  variant="contained"
                  fullWidth
                  sx={{
                    width: '100%',
                    height: '36px', // Compact height
                    borderRadius: '8px', // Adjust radius (e.g., '4px', '12px', '24px' for pill shape)
                    fontSize: '0.875rem', // Smaller text
                    textTransform: 'none', // Disable uppercase
                    py: 1, // Tighter vertical padding
                    px: 2, // Tighter horizontal padding
                    '&:hover': {
                      backgroundColor: 'primary.dark', // Optional: Darken hover effect
                    },
                  }}
                  // disabled={loading}
                  endIcon={loading ? <CircularProgress size={24} /> : <CheckCircleFilled />}
                >
                  Submit
                </Button>
              </form>
              <Button
                color="secondary"
                onClick={() => setShowForgotPassword(true)}
                sx={{ mt: 2, borderRadius: 25, color: 'white' }}
                fullWidth
              >
                Forgot Password?
              </Button>

              <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                {message}
              </Typography>
            </>
          ) : otpSent ? (
            <>
              <Typography
                variant="h6"
                align="center"
                sx={{ mb: 4, fontFamily: 'Roboto, sans-serif', color: '#FFFFFF' }}
              >
                RESET PASSWORD
              </Typography>
              <TextField
                id="otp"
                label="OTP"
                variant="outlined"
                value={resetToken}
                onChange={handleOtpChange}
                fullWidth
                sx={{
                  mb: 2,
                  backgroundColor: 'white', // Set background color to white
                }}
                autoComplete="off"
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                error={!!errors.resetToken}
                helperText={errors.resetToken}
              />
              <Button
                color="primary"
                onClick={handleValidateOtp}
                variant="contained"
                fullWidth
                sx={buttonStyle}
                style={{
                  backgroundColor: otpValid ? '#4CAF50' : '#f0f0f0', // Green if OTP is valid, light gray if not
                  color: otpValid ? 'white' : '#333', // White text if OTP is valid, dark text if not
                  border: 'none',
                  padding: '10px 20px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.3s ease',
                }}
                disabled={!resetToken}
              >
                Validate OTP
              </Button>
              <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                {message}
              </Typography>
              {otpValid && (
                <>
                  <TextField
                    id="new-password"
                    label="New Password"
                    type="password"
                    variant="outlined"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    sx={{
                      mb: 2,
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ccc',
                        },
                        '&:hover fieldset': {
                          borderColor: '#999',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        fontSize: '0.875rem', // Adjust this value to your preference (default is 1rem)
                        opacity: 1, // Ensure the placeholder is fully visible
                      },
                    }}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword}
                    autoComplete="off" // Disable autofill
                  />
                  <TextField
                    id="confirm-password"
                    label="Confirm Password"
                    type="password"
                    variant="outlined"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    sx={{
                      mb: 2,
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ccc',
                        },
                        '&:hover fieldset': {
                          borderColor: '#999',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        fontSize: '0.875rem', // Adjust this value to your preference (default is 1rem)
                        opacity: 1, // Ensure the placeholder is fully visible
                      },
                    }}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    autoComplete="off"
                  />
                  <Button
                    color="primary"
                    onClick={handleResetPassword}
                    variant="contained"
                    fullWidth
                    size="small"
                    sx={{
                      width: '100%',
                      height: '32px', // Reduced height
                      fontSize: '0.8125rem', // 13px - smaller text
                      borderRadius: '4px', // Subtle rounded corners
                      textTransform: 'none', // Prevent uppercase transformation
                      padding: '6px 12px', // Compact padding
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                    disabled={!newPassword || !confirmPassword}
                  >
                    Reset Password
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <Typography
                variant="h6"
                align="center"
                sx={{ mb: 4, fontFamily: 'Roboto, sans-serif', color: '#FFFFFF' }}
              >
                FORGOT PASSWORD
              </Typography>
              <TextField
                id="forgot-username"
                placeholder="Username"
                variant="outlined"
                onChange={(event) => setUsername(event.target.value)}
                value={username}
                fullWidth
                sx={{
                  width: '100%',
                  mb: 2,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ccc',
                    },
                    '&:hover fieldset': {
                      borderColor: '#999',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: '8px', // Reduce vertical padding to decrease height
                    fontSize: '0.875rem', // Optional: Adjust input text size
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.75rem', // Smaller placeholder font size
                    opacity: 1,
                  },
                }}
                autoComplete="off"
                error={!!errors.username}
                helperText={errors.username}
              />
              <TextField
                id="forgot-email"
                placeholder="Email"
                variant="outlined"
                onChange={(event) => setEmail(event.target.value)}
                value={email}
                fullWidth
                sx={{
                  width: '100%',
                  mb: 2,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ccc',
                    },
                    '&:hover fieldset': {
                      borderColor: '#999',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: '8px', // Reduce vertical padding to decrease height
                    fontSize: '0.875rem', // Optional: Adjust input text size
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.75rem', // Smaller placeholder font size
                    opacity: 1,
                  },
                }}
                autoComplete="off"
                error={!!errors.email}
                helperText={errors.email}
              />
              <Button
                color="primary"
                onClick={handleForgotPassword}
                variant="contained"
                fullWidth
                // sx={buttonStyle}
                size="small"
                sx={{
                  width: '100%',
                  height: '32px', // Reduced height
                  fontSize: '0.8125rem', // 13px - smaller text
                  borderRadius: '4px', // Subtle rounded corners
                  textTransform: 'none', // Prevent uppercase transformation
                  padding: '6px 12px', // Compact padding
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
                disabled={!username || !email}
              >
                Send OTP
              </Button>
              <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                {message}
              </Typography>
            </>
          )}
      </Paper>
    </Box>
  );
}

export default Login;
